import {
  ChangeEventHandler,
  forwardRef,
  KeyboardEventHandler,
  memo,
  MouseEventHandler,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';

import { Icon } from '@/shared/uikit/Icon/index';

import { FieldSelectView } from './FieldSelectView';
import { useClickOutSide, useListScrollByKeyBoard } from './selectHooks';
import { BaseSelectProps, Option } from './types';

import { EmptyCase, List, MainWrapper, OptionItemSingle } from './styles';

export type SelectSingleProps = BaseSelectProps & {
  // main props
  value: Option['value'] | null;
  onChange?: (value: Option['value'] | null) => void;
  // select manage props
  keyBoardHandlers: (
    commitSelection: (idx: number | null) => void,
    listLength?: number,
  ) => KeyboardEventHandler;
  onToggleList: () => void;
  onClickReset: MouseEventHandler;
  onKeyDownReset: KeyboardEventHandler;
  open: boolean;
  activeIndex: number | null;
  className?: string;
};

const SelectSingleInner = forwardRef<HTMLSelectElement, SelectSingleProps>(
  (
    {
      // main props
      label,
      name,
      options,
      value,
      status,
      onChange,
      disabled = false,
      required,
      errorText,
      iconAfter,
      withSearch,
      className,
      // select manage props
      keyBoardHandlers,
      onToggleList,
      onClickReset,
      onKeyDownReset,
      open,
      activeIndex,
    },
    ref,
  ) => {
    const listboxId = useId();
    const labelId = useId();
    const optionIdBase = useId();
    const errorTextId = useId();
    const fieldViewRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const searchViewRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const hasError = status === 'error';
    const activeId = open ? `${optionIdBase}-${activeIndex}` : undefined;

    const safeValue = ['', NaN, null, undefined].includes(value) ? null : value;

    const selectedOption = useMemo(
      () => options.find((opt) => opt.value === safeValue) ?? null,
      [options, safeValue],
    );
    const selectedLabel = selectedOption?.content.label;
    const [inputValue, setInputValue] = useState(safeValue ?? '');
    const [filterValue, setFilterValue] = useState('');
    const normalizedFilter =
      withSearch && open && filterValue.trim().length > 0 ? filterValue.trim().toLowerCase() : '';

    const optionsToRender = useMemo(() => {
      if (!withSearch || !open || !normalizedFilter) {
        return options;
      }
      return options.filter((opt) => opt.content.label.toLowerCase().includes(normalizedFilter));
    }, [normalizedFilter, options, open, withSearch]);

    useEffect(() => {
      if (!withSearch) return;

      if (!open && options.some((o) => o.value === safeValue)) {
        setInputValue(selectedLabel ?? '');
      }
    }, [open, safeValue]);

    // Close by clicking outside
    useClickOutSide({ open, onToggleList, fieldViewRef, listRef });

    // Scroll by using (ArrowUp | ArrowDown | Home | End) keys
    useListScrollByKeyBoard({ activeIndex, open, optionRefs });

    const commitSelection = (idx: number | null) => {
      if (idx === null) return;
      const opt = optionsToRender[idx];
      if (!opt) return;

      onChange?.(opt.value);
      setInputValue(opt.content.label);
      setFilterValue('');
      onToggleList();
    };
    const handleKeyDown = keyBoardHandlers(commitSelection, optionsToRender.length);

    const handleSearch: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
      setInputValue(target.value);
      setFilterValue(target.value);
    };

    const handleResetClick: MouseEventHandler = (e) => {
      onClickReset(e);
      setInputValue('');
      setFilterValue('');
    };

    const handleResetKeyDown: KeyboardEventHandler = (e) => {
      onKeyDownReset(e);
      if ([' ', 'Enter'].includes(e.key)) {
        setInputValue('');
        setFilterValue('');
      }
    };

    const handleFieldClick = () => {
      if (!open && withSearch) {
        searchViewRef.current?.focus();
      }
      onToggleList();
    };

    return (
      <MainWrapper className={cn(className, { required })}>
        <div className="select-wrapper">
          <FieldSelectView
            className="field-view"
            ref={fieldViewRef}
            labelId={labelId}
            listboxId={listboxId}
            activeId={activeId}
            hasError={hasError}
            errorTextId={errorTextId}
            onKeyDown={handleKeyDown}
            onFieldClick={handleFieldClick}
            onKeyDownReset={handleResetKeyDown}
            onClickReset={handleResetClick}
            onSearch={handleSearch}
            label={label}
            selectedView={selectedLabel ?? ''}
            iconAfter={iconAfter}
            status={status}
            disabled={disabled}
            required={required}
            open={open}
            withSearch={withSearch}
            searchViewRef={searchViewRef}
            inputValue={inputValue}
          />

          {/* Popup-list */}
          {open && (
            <List
              className="uikitScrollbar"
              tabIndex={-1}
              ref={listRef}
              role="listbox"
              id={listboxId}
              aria-labelledby={labelId}
            >
              {optionsToRender.length > 0 ? (
                optionsToRender.map((opt, idx) => {
                  const optionId = `${optionIdBase}-${idx}`;
                  const selected = opt.value === safeValue;
                  const active = idx === activeIndex;

                  return (
                    <OptionItemSingle
                      ref={(el) => {
                        optionRefs.current[idx] = el;
                      }}
                      id={optionId}
                      key={opt.value}
                      role="option"
                      aria-selected={selected}
                      onClick={() => commitSelection(idx)}
                    >
                      <div className="option-content">
                        <div className="option-label">{opt.content.label}</div>
                        {Boolean(opt.content.description) && (
                          <div className="option-description">{opt.content.description}</div>
                        )}
                      </div>
                      {(selected || active) && (
                        <Icon name="check" className="check" size="s20" aria-hidden="true" />
                      )}
                    </OptionItemSingle>
                  );
                })
              ) : (
                <EmptyCase>No matches found for your search</EmptyCase>
              )}
            </List>
          )}

          {/* Native select hidden */}
          <select
            ref={ref}
            name={name}
            value={safeValue ?? undefined}
            onChange={() => {}}
            tabIndex={-1}
            className="oouk-select-visually-hidden"
            required={required}
            disabled={disabled}
          >
            {options.map((opt, idx) => (
              <option key={idx} value={opt.value}>
                {opt.content.label}
              </option>
            ))}
          </select>
        </div>

        {hasError && (
          <div className="error-text" id={errorTextId} role="alert">
            {errorText}
          </div>
        )}
      </MainWrapper>
    );
  },
);

SelectSingleInner.displayName = 'SelectSingle';
export const SelectSingle = memo(SelectSingleInner) as typeof SelectSingleInner;
